import React, { useEffect, useState } from "react";
import { api, formatCurrency } from "@/lib/api";
import { logger } from "@/lib/logger";
import { useNavigate } from "react-router-dom";
import {
  LineChart as LineChartIcon,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  ZapOff,
  Target,
} from "lucide-react";
import PriceHistoryDialog from "@/components/PriceHistoryDialog";
import { useAuth } from "@/context/AuthContext";

const MAX_CARDS = 5;

const TYPE_LABEL = { stock: "Acciones", fund: "Fondos", crypto: "Cripto" };

/**
 * Large, tappable watchlist cards for the mobile home. Each card shows a
 * ticker's current price + today's variation. Tap opens the full history
 * modal. Order: biggest daily movers first.
 */
export default function MobileWatchlistCards() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyItem, setHistoryItem] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [allInvestments, setAllInvestments] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [wl, inv] = await Promise.all([
          api.get("/investments/watchlist"),
          api.get("/investments"),
        ]);
        if (cancelled) return;
        setItems(wl.data?.items || []);
        setAllInvestments(inv.data || []);
      } catch (e) {
        logger.warn("mobile watchlist", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="md:hidden" data-testid="mobile-watchlist-loading">
        <div className="h-24 rounded-2xl bg-[#EBEBEA]/60 animate-pulse" />
      </div>
    );
  }

  if (!items.length) return null;

  const open = (it) => {
    const full = allInvestments.find((i) => i.id === it.id) || it;
    setHistoryItem(full);
    setHistoryOpen(true);
  };

  const visible = items.slice(0, MAX_CARDS);

  return (
    <div className="md:hidden flex flex-col gap-3" data-testid="mobile-watchlist">
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2 text-[#8A9992]">
          <LineChartIcon className="w-4 h-4" />
          <span className="text-[10px] uppercase tracking-[0.14em] font-bold">Watchlist</span>
        </div>
        {items.length > MAX_CARDS && (
          <button
            type="button"
            onClick={() => nav("/watchlist")}
            data-testid="mobile-watchlist-see-all"
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#1C7C54]"
          >
            Ver todos <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {visible.map((it) => {
        const pct = it.change_pct;
        const up = (pct ?? 0) >= 0;
        const abs = it.change_abs;
        return (
          <button
            key={it.id}
            type="button"
            data-testid={`mobile-watchlist-card-${it.id}`}
            onClick={() => open(it)}
            className="text-left card-surface p-4 active:scale-[0.99] transition-transform"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="text-base font-semibold text-[#0A110E] truncate">
                    {it.name}
                  </div>
                  {it.is_stale && (
                    <ZapOff className="w-3.5 h-3.5 text-[#D95D39] shrink-0" />
                  )}
                </div>
                <div className="text-[11px] text-[#8A9992] mt-0.5 truncate">
                  {TYPE_LABEL[it.type] || it.type}
                  {it.ticker && <span> · {it.ticker.toUpperCase()}</span>}
                  {it.isin && <span> · {it.isin}</span>}
                </div>
              </div>
              {pct != null ? (
                <div
                  className={`inline-flex items-center gap-1 shrink-0 h-7 px-2 rounded-full text-xs font-semibold ${
                    up ? "bg-[#1C7C54]/10 text-[#1C7C54]" : "bg-[#D95D39]/10 text-[#D95D39]"
                  }`}
                  data-testid={`mobile-watchlist-pct-${it.id}`}
                >
                  {up ? (
                    <TrendingUp className="w-3.5 h-3.5" />
                  ) : (
                    <TrendingDown className="w-3.5 h-3.5" />
                  )}
                  {up ? "+" : ""}{pct.toFixed(2)}%
                </div>
              ) : (
                <span className="text-[11px] text-[#8A9992]">sin datos</span>
              )}
            </div>

            <div className="flex items-baseline justify-between gap-3 mt-3">
              <div className="font-display text-3xl font-semibold tabular-nums text-[#0A110E]">
                {formatCurrency(it.current_price, it.currency)}
              </div>
              {abs != null && (
                <div
                  className={`text-sm font-medium tabular-nums ${
                    up ? "text-[#1C7C54]" : "text-[#D95D39]"
                  }`}
                >
                  {up ? "+" : ""}{formatCurrency(abs, it.currency)}
                </div>
              )}
            </div>

            {(it.target_price_up || it.target_price_down) && (
              <div
                data-testid={`mobile-watchlist-targets-${it.id}`}
                className="mt-2 flex items-center gap-2 text-[11px] text-[#8A9992]"
              >
                <Target className="w-3 h-3 text-[#8B6B1F]" />
                {it.target_price_up && (
                  <span>↑ {formatCurrency(it.target_price_up, it.currency)}</span>
                )}
                {it.target_price_down && (
                  <span>↓ {formatCurrency(it.target_price_down, it.currency)}</span>
                )}
              </div>
            )}
          </button>
        );
      })}

      <PriceHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        investment={historyItem}
        baseCurrency={user?.base_currency}
        allInvestments={allInvestments}
      />
    </div>
  );
}
